import { useCallback, useRef, useState } from "react";
import {
  updateTemporalData,
  azurecycleAkanthasSendBusqueueRequest,
} from "../../../services/container";

const extractImageName = (imageUrl) =>
  imageUrl?.split("?")[0]?.split("pictures/")[1]?.replace(".jpeg", "");

const pairKey = (pair) => pair?.after?._time;

/**
 * Lets the operator mark a detected compaction as invalid (it will stop
 * counting as a real packmat everywhere in the app) or restore it, using
 * the same `packmat` flag and update route as the backoffice's "Check/Edit
 * Temporal Data" screen. Keeps a one-step undo stack, and excluded pairs
 * are kept client-side (via `excludedPairs`) so they stay visible/greyed
 * out instead of disappearing once the server stops returning them.
 */
export const useCompactionExclusions = ({ onAfterChange }) => {
  const [excludedPairs, setExcludedPairs] = useState([]);
  const [pendingKey, setPendingKey] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [canUndo, setCanUndo] = useState(false);
  const undoStackRef = useRef([]);

  const persistPackmatValue = useCallback(async (pair, nextValue) => {
    const dataLineUpdate = { ...pair.after, packmat: nextValue };
    await updateTemporalData(pair.after._measurement, dataLineUpdate);

    const imageName = extractImageName(pair.after.image_url);
    if (imageName) {
      await azurecycleAkanthasSendBusqueueRequest(
        { queueName: "statisticsQueue" },
        imageName,
      );
    }
  }, []);

  const setPairExcluded = useCallback(
    async (pair, excluded, { trackUndo = true } = {}) => {
      const key = pairKey(pair);
      setPendingKey(key);
      setErrorKey(null);

      setExcludedPairs((current) =>
        excluded
          ? [pair, ...current.filter((item) => pairKey(item) !== key)]
          : current.filter((item) => pairKey(item) !== key),
      );

      try {
        await persistPackmatValue(pair, !excluded);
        if (trackUndo) {
          undoStackRef.current.push({ pair, wasExcluded: !excluded });
          setCanUndo(true);
        }
        await onAfterChange?.();
      } catch (error) {
        // rollback the optimistic state on failure
        setExcludedPairs((current) =>
          excluded
            ? current.filter((item) => pairKey(item) !== key)
            : [pair, ...current.filter((item) => pairKey(item) !== key)],
        );
        setErrorKey(key);
      } finally {
        setPendingKey(null);
      }
    },
    [onAfterChange, persistPackmatValue],
  );

  const undo = useCallback(async () => {
    const last = undoStackRef.current.pop();
    if (!last) return;
    setCanUndo(undoStackRef.current.length > 0);
    await setPairExcluded(last.pair, last.wasExcluded, { trackUndo: false });
  }, [setPairExcluded]);

  return {
    excludedPairs,
    setPairExcluded,
    undo,
    canUndo,
    pendingKey,
    errorKey,
  };
};
